import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject, Subscription, debounceTime, finalize } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { AddStudentModalComponent } from '../../components/modals/add-student-modal/add-student-modal.component';
import { CollegePortalService } from '../../../college-workspace/services/college-portal.service';
import { StudentListItem, StudentListParams } from '../../../college-workspace/models/college-portal.models';
import { CollegeDashboardService } from '../../services/college-dashboard.service';
import { parseYearOfPassing } from '../../utils/academic-year.util';

@Component({
  selector: 'app-college-students',
  standalone: true,
  imports: [CommonModule, FormsModule, AddStudentModalComponent],
  templateUrl: './college-students.component.html',
  styleUrl: './college-students.component.scss'
})
export class CollegeStudentsComponent implements OnInit, OnDestroy {
  @Output() viewStudentByUserId = new EventEmitter<string>();

  isAddStudentModalOpen = false;

  query: string = '';
  // Course/year filtering now comes from the shared header (Academic Year /
  // Department selectors) rather than a second, duplicate dropdown here —
  // see loadStudents().
  private course?: string;
  private yearOfPassing?: number;

  // Not currently shown — readiness and placement-status columns/filters
  // have no real backend source yet (see college-students.component.html).
  // readinessFilter: string = 'All Readiness';
  // statusFilter: string = 'All Students';
  // readinessOptions: string[] = ['All Readiness', 'Ready', 'Developing', 'Needs Improvement'];
  // statusOptions: string[] = ['All Students', 'Unplaced', 'In Process', 'Placed', 'Not Eligible'];

  students: StudentListItem[] = [];
  total = 0;
  page = 1;
  limit = 10;
  totalPages = 1;
  isLoading = false;

  private searchSubject = new Subject<void>();
  private subscription = new Subscription();

  constructor(
    private collegeService: CollegePortalService,
    private dashboardService: CollegeDashboardService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.subscription.add(
      this.searchSubject.pipe(debounceTime(400)).subscribe(() => {
        this.page = 1;
        this.loadStudents();
      })
    );
    this.subscription.add(
      this.dashboardService.settings$.subscribe((settings) => {
        this.course = settings.department !== 'All Departments' ? settings.department : undefined;
        this.yearOfPassing = parseYearOfPassing(settings.academicYear);
        this.page = 1;
        this.loadStudents();
      })
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  get pageNumbers(): number[] {
    const windowSize = 5;
    let start = Math.max(1, this.page - Math.floor(windowSize / 2));
    const end = Math.min(this.totalPages, start + windowSize - 1);
    start = Math.max(1, end - windowSize + 1);
    const pages: number[] = [];
    for (let p = start; p <= end; p++) pages.push(p);
    return pages;
  }

  getInitials(firstName: string, lastName: string): string {
    return `${firstName?.[0] ?? ''}${lastName?.[0] ?? ''}`.toUpperCase();
  }

  onSearchInput(): void {
    this.searchSubject.next();
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages) return;
    this.page = page;
    this.loadStudents();
  }

  onViewStudent(student: StudentListItem): void {
    this.viewStudentByUserId.emit(student.student_user_id);
  }

  onAddStudent(): void {
    this.isAddStudentModalOpen = true;
  }

  onStudentAdded(): void {
    this.page = 1;
    this.loadStudents();
  }

  loadStudents(): void {
    this.isLoading = true;
    const params: StudentListParams = {
      search: this.query.trim() || undefined,
      course: this.course,
      year_of_passing: this.yearOfPassing,
      page: this.page,
      limit: this.limit
    };

    this.collegeService.getStudents(params).pipe(
      finalize(() => { this.isLoading = false; })
    ).subscribe({
      next: (res) => {
        if (res.Success && res.Data) {
          this.students = res.Data.items;
          this.total = res.Data.total;
          this.totalPages = res.Data.total_pages;
        } else {
          this.toastr.error(res.Message || 'Failed to load students.');
        }
      },
      error: () => {
        this.toastr.error('Failed to load students.');
      }
    });
  }
}
